/**
 * Two-frame media swap for the Vela editorial cards.
 *
 * `data-swap-mode="hover"` (runway): desktop swapping is pure CSS, so this
 * element only covers touch, where the first tap on the card link swaps the
 * image and the second one follows the link.
 *
 * `data-swap-mode="click"` (categories): the image never reacts to hover. A
 * transparent button over the media toggles the two frames, which leaves the
 * heading link as the only way into the collection.
 */
class VelaMediaSwap extends HTMLElement {
  connectedCallback() {
    if (this.dataset.swapMode === 'click') {
      this.#connectToggle();
    } else {
      this.#connectTouchLink();
    }
  }

  disconnectedCallback() {
    this.#teardown?.();
    this.#teardown = null;
  }

  #teardown = null;

  get swapped() {
    return this.classList.contains('is-swapped');
  }

  set swapped(value) {
    this.classList.toggle('is-swapped', value);
    const toggle = this.querySelector('[data-swap-toggle]');
    if (toggle) toggle.setAttribute('aria-pressed', String(value));
  }

  #connectToggle() {
    const toggle = this.querySelector('[data-swap-toggle]');
    if (!toggle) return;

    const onClick = () => {
      this.swapped = !this.swapped;
    };

    toggle.addEventListener('click', onClick);
    this.#teardown = () => toggle.removeEventListener('click', onClick);
  }

  #connectTouchLink() {
    const link = this.closest('.vela-editorial__card')?.querySelector('.vela-editorial__card-link');
    if (!link) return;

    const onClick = (event) => {
      // A device that can hover has already shown the second frame, so the tap
      // is a real navigation.
      if (window.matchMedia('(hover: hover)').matches || this.swapped) return;
      event.preventDefault();
      this.swapped = true;
    };

    link.addEventListener('click', onClick);
    this.#teardown = () => link.removeEventListener('click', onClick);
  }
}

if (!customElements.get('vela-media-swap')) {
  customElements.define('vela-media-swap', VelaMediaSwap);
}
